/**
 * Hub post-login: pantalla de entrada donde el usuario elige entorno
 * (Recibidas, Emitidas, Datos de empresa) tras iniciar sesión.
 */
import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  CalendarClock,
  FileStack,
  FileSpreadsheet,
  FolderOpen,
  LayoutDashboard,
  Shield,
  LogOut,
  Compass,
  Landmark,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import ReleaseNotesBanner from "@/components/ReleaseNotesBanner";
import EntryCard from "@/components/EntryCard";
import { countDuePayments } from "@/lib/scheduledPayments";
import OnboardingTour from "@/components/OnboardingTour";
import { markOnboardingSeen, wasOnboardingSeen } from "@/lib/onboarding";


const Hub = () => {
  const { user, isAdmin, canAccessIssuedInvoices, canAccessFinancing, canAccessPayments, signOut } = useAuth();
  const [duePayments, setDuePayments] = useState(0);
  useEffect(() => {
    if (canAccessPayments) countDuePayments().then(setDuePayments).catch(() => {});
  }, [canAccessPayments]);
  const navigate = useNavigate();
  const [tourOpen, setTourOpen] = useState(false);

  const name = (user?.user_metadata?.full_name as string | undefined) || user?.email?.split("@")[0] || "";

  useEffect(() => {
    if (!wasOnboardingSeen()) setTourOpen(true);
  }, []);

  const closeTour = () => {
    markOnboardingSeen();
    setTourOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };


  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* Logo placeholder removed at user request */}
            <div className="min-w-0">
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-1.5" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 md:py-16">
        <ReleaseNotesBanner className="mb-8" />

        <div className="mb-10 md:mb-14">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight font-[Playfair_Display,serif]">
            Hola{name ? `, ${name}` : ""}.
          </h1>
          <p className="text-muted-foreground mt-2 text-base md:text-lg">
            ¿Qué vas a hacer hoy?
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <EntryCard
            to="/app"
            icon={FileStack}
            title="Procesador recibidas"
            description="Extrae y contabiliza facturas de proveedores desde PDF o Excel a SUENLACE."
          />
          <EntryCard
            to={canAccessIssuedInvoices ? "/emitidas" : undefined}
            icon={FileSpreadsheet}
            title="Procesador emitidas"
            description="Extrae y contabiliza facturas de clientes desde PDF o Excel a SUENLACE."
            disabled={!canAccessIssuedInvoices}
            disabledHint="Sin acceso. Pide al administrador que te habilite Facturas emitidas."
          />
          <EntryCard
            to="/datos-empresa"
            icon={FolderOpen}
            title="Datos de empresa"
            description="Histórico de facturas, maestros de proveedores y clientes, y cuadros de financiación."
          />
          <EntryCard
            to={canAccessPayments ? "/pagos" : undefined}
            icon={CalendarClock}
            title={canAccessPayments && duePayments > 0 ? `Agenda de pagos · ${duePayments} para hoy o vencidos` : "Agenda de pagos"}
            description="Calendario de vencimientos con la factura adjunta, cuenta de pago y quién autoriza."
            disabled={!canAccessPayments}
            disabledHint="Sin acceso. Pide al administrador que te habilite la Agenda de pagos."
          />
          {isAdmin && (
            <EntryCard
              to="/bancos"
              icon={Landmark}
              title="Bancos (Norma 43)"
              description="Importa extractos bancarios N43/SEPA, desglosa préstamos y cobros de clientes, y genera el SUENLACE para A3ECO."
            />
          )}

        </div>

        <div className="mt-12 pt-6 border-t">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3">
            Accesos rápidos
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <NavLink to="/dashboard">
                <LayoutDashboard className="h-4 w-4 mr-1.5" />
                Panel de control
              </NavLink>
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" asChild>
                <NavLink to="/admin">
                  <Shield className="h-4 w-4 mr-1.5" />
                  Administración
                </NavLink>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setTourOpen(true)}>
              <Compass className="h-4 w-4 mr-1.5" />
              Ver recorrido
            </Button>
          </div>
        </div>

      </main>

      <OnboardingTour
        open={tourOpen}
        onClose={closeTour}
        name={name}
        canAccessIssuedInvoices={canAccessIssuedInvoices}
        canAccessFinancing={canAccessFinancing}
      />
    </div>
  );

};

export default Hub;
